import type { HomeSkill } from '@/lib/api/home'
import { getSkillColor } from './utils'
import { accountedWidth, cardFootnote, coverageNote, hasAnyRecord, needsCaveat } from './skillEvidence'

interface Props {
  skills: HomeSkill[]
}

export function ProgressSkillsCard({ skills }: Props) {
  if (skills.length === 0) return null

  const sorted = [...skills].sort((a, b) => b.score - a.score)
  const top5 = sorted.slice(0, 5)
  const weakest = sorted[sorted.length - 1]
  // Card-level, not per-skill: before any telemetry exists every bar would
  // otherwise render fully shaded, which reads as a broken chart rather than
  // as a caveat.
  const anyRecord = hasAnyRecord(skills)
  const footnote = cardFootnote(skills)

  return (
    <section className="rounded-xl border border-[#e8e4df] bg-white p-6" aria-label="Skills">
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-4">Skills</p>
      <div className="flex flex-col gap-3">
        {top5.map((skill) => {
          const color = getSkillColor(skill.score)
          const ev = skill.evidence
          const note = ev ? coverageNote(ev, anyRecord) : null
          const split = ev ? needsCaveat(ev, anyRecord) : false
          // The solid portion is what we can vouch for; the hatched remainder
          // is the part of the score we have no record behind. Splitting the
          // same bar rather than drawing a second one keeps this a caveat on
          // the number, not a competing measurement.
          const solid = ev && split ? accountedWidth(skill.score, ev) : skill.score
          return (
            <div key={skill.name} className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <span className="w-32 flex-shrink-0 text-xs text-[#9a9589] leading-tight">{skill.name}</span>
                <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-[#f2f0ed]">
                  <div
                    className="h-full transition-all duration-300"
                    style={{ width: `${solid}%`, background: color }}
                  />
                  {split && (
                    // Striped in the skill's own colour rather than in grey:
                    // this is the same measurement, less certain — not a
                    // different, greyer quantity sitting next to it.
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${Math.max(0, skill.score - solid)}%`,
                        backgroundImage: `repeating-linear-gradient(45deg, ${color}b3 0 2px, ${color}33 2px 5px)`,
                      }}
                    />
                  )}
                </div>
                <span className="w-7 flex-shrink-0 text-right text-xs font-bold" style={{ color }}>
                  {Math.round(skill.score)}
                </span>
              </div>
              {note && (
                <span className="ml-[8.75rem] text-[11px] leading-snug text-[#9a9589]">{note}</span>
              )}
            </div>
          )
        })}
      </div>
      {footnote && (
        <p className="mt-3 border-t border-[#f2f0ed] pt-3 text-[11px] leading-snug text-[#9a9589]">
          {footnote}
        </p>
      )}
      {weakest.score < 70 && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
          <span aria-hidden="true">⚠</span>
          <span><strong>Focus area:</strong> {weakest.name} · score {weakest.score}/100</span>
        </div>
      )}
    </section>
  )
}
